/**
 * Product
 *
 * @module product
 */
const component = {

    /**
     * Init component
     * 
     * @returns {undefined}
     */
    init: () => {

        /**
         * Product module api
         * 
         * @global
         */
        window.theme.product = component;
    },

    /**
     * Load product object by handle.
     * 
     * @public
     * @method
     * @name loadProduct
     * @todo Implement a test for this function.
     * @param {string} handle Product handle.
     * @param {string} extended Extend product data.
     * @returns {Promise} Promise object represents a product.
     */
    loadProduct: (handle, extended) => {
        
        return new Promise((resolve, reject) => {
            
            let cache = component.getCache(handle);
        
            if (cache) {

                if (!cache.extended && extended) {

                    component.request(handle, extended).then(product => {
                        resolve(product);
                    });

                }  else {
                
                    resolve(cache);
                }

            } else {
                
                component.request(handle, extended).then(product => {

                    resolve(product);
                });
            }

        });
    },

    /**
     * Make a request to get product data.
     * 
     * @public
     * @method
     * @name request
     * @todo Implement a test for this function.
     * @param {string} handle Product handle.
     * @param {string} extended Extend product data.
     * @returns {Promise} Promise object represents a product.
     */
    request: (handle, extended) => {

        return new Promise((resolve, reject) => {

            let url = (extended) ? '/products/' + handle + '?view=json' : '/products/' + handle + '.json';

            fetch(url).then(resp => {

                return resp.json();
    
            }).then(data => {
    
                if (data.product) {

                    data = component.normalizeData(data);

                    component.cache(data.product);

                    resolve(data.product);

                } else {

                    reject(data);
                }
            }).catch(function(error) {
                console.log(error);
                reject(error);
            });
        });
    },

    /**
     * Get product data by handle and metafield (namespace and key) using GRAPHQL query.
     *
     * @public
     * @method
     * @name getProductMetafieldsByHandle
     * @param {string} handle Product handle.
     * @param {string} namespace Metafield namespace.
     * @param {string} key Metafield key.
     * @returns {Promise} Promise object represents the metafields of the product.
     */
    getProductMetafieldsByHandle(handle, namespace, key) {
        return new Promise((resolve, reject) => {
            const productQuery = `
                query {
                    product(handle: "${handle.trim()}"){
                        metafields(identifiers: [{ namespace:"${namespace}",key:"${key}"}]){
                            id
                            namespace
                            key
                            value
                        }
                    }
                }
            `;

            // Ensure API credentials are available
            if (window.theme.frontend_api && window.theme.api_version) {

                // Fetch data
                const STOREFRONT_ACCESS_TOKEN = window.theme.frontend_api;
                const GRAPHQL_URL = "/api/" + window.theme.api_version + "/graphql.json";

                fetch(GRAPHQL_URL, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/graphql",
                        "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
                    },
                    body: productQuery,
                })
                .then((res) => res.json())
                .then((result) => {
                    if (result?.data?.product) {
                        const metafields = result.data.product.metafields;
                        resolve(metafields);
                    } else {
                        reject("No metafields found.");
                    }
                })
                .catch((error) => {
                    console.error("Error fetching product metafields:", error);
                    reject("Error fetching product metafields.");
                });

            } else {
                console.error("Please set the frontend_api and api_version in the window.theme object");
                reject("Missing API credentials.");
            }
        });
    },


    /**
     * Normalize product data.
     * 
     * @private
     * @method
     * @name normalizeData
     * @todo Implement a test for this function.
     * @param {object} data Request data.
     * @returns {object} Normalized data.
     */
    normalizeData: data => {

        // Fix options
        if (data.options_with_values) {
            data.product.options = data.options_with_values;
        }

        // Add options by name
        if (data.options_by_name) {
            data.product.options_by_name = data.options_by_name;
        }

        // Add extended flag
        if (data.extended) {
            data.product.extended = true;
        }

        // Fix images
        if (data.product.extended) {
            data.product.images = data.product.media;
        }

        return data;
    },

    /**
     * Check if a product has variants.
     * 
     * @public
     * @method
     * @name hasVariants
     * @param {object} product Product data.
     * @returns {boolean} Product has variants or not.
     */
    hasVariants: product => {

        if(product.options.length == 1 && product.options[0].values.length == 1) {

            return false;

        } else {

            return true;
        }
    },

    /**
     * Chace a product data.
     * 
     * @private
     * @method
     * @name request
     * @param {object} product Product data.
     * @returns {boolean}
     */
    cache: product => {

        let productValue = JSON.stringify(product);

        if (product.handle && productValue.length < 50000) {

            try {

                sessionStorage.setItem(product.handle, productValue);
    
                return true;
                
            } catch (error) {
                
                return false;
            }

        } else {

            return false;
        }
    },

    /**
     * Get product from cache.
     * 
     * @private
     * @method
     * @name request
     * @param {string} handle Product handle.
     * @param {string} extended Extend product data.
     * @returns {Promise} Promise object represents a product.
     */
    getCache: handle => {

        return JSON.parse(sessionStorage.getItem(handle));
    },

    /**
     * Check if the product is coming soon
     * 
     * @private
     * @method
     * @name isComingSoon
     * @param {object} product Product data.
     * @returns {boolean}
     */
    isComingSoon: product => {
        
        let result = false,
            productEvent = window.shopEvents[window.theme.handleize(product.vendor)];

        if (typeof productEvent != 'undefined') {

            if ( productEvent.status == 'coming-soon') {
                result = true;
            }
        }

        return result;
    }
};

export default component;
